import { useState, useCallback, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

export const FILTER_SET_NAME_MAX_LENGTH = 80;

interface FilterNameFormValues {
  filterSetName: string;
}

interface UseFilterNameFormOptions {
  initialName?: string;
  initialDefault?: boolean;
}

interface UseFilterNameFormReturn {
  filterName: string;
  isDefault: boolean;
  isSaveDisabled: boolean;
  formMethods: UseFormReturn<FilterNameFormValues>;
  handleDefaultChange: () => void;
  handleSave: (onSave: (name: string, isDefault: boolean) => void | Promise<void>) => void;
  reset: (name?: string, isDefault?: boolean) => void;
}

const useFilterNameForm = ({
  initialName = '',
  initialDefault = false,
}: UseFilterNameFormOptions = {}): UseFilterNameFormReturn => {
  const [filterName, setFilterName] = useState(initialName);
  const [isDefault, setIsDefault] = useState(initialDefault);
  const formMethods = useForm<FilterNameFormValues>({ defaultValues: { filterSetName: initialName } });

  useEffect(() => {
    const subscription = formMethods.watch(values => {
      setFilterName(values.filterSetName ?? '');
    });
    return () => subscription.unsubscribe();
  }, [formMethods]);

  const handleDefaultChange = useCallback(() => {
    setIsDefault(prev => !prev);
  }, []);

  const handleSave = useCallback(
    (onSave: (name: string, isDefault: boolean) => void | Promise<void>) => {
      const trimmed = filterName.trim();
      if (trimmed.length > 0) {
        onSave(trimmed, isDefault);
      }
    },
    [filterName, isDefault],
  );

  const reset = useCallback(
    (name: string = '', defaultValue: boolean = false) => {
      setFilterName(name);
      setIsDefault(defaultValue);
      formMethods.reset({ filterSetName: name });
    },
    [formMethods],
  );

  const isSaveDisabled = filterName.trim().length === 0;

  return {
    filterName,
    isDefault,
    isSaveDisabled,
    formMethods,
    handleDefaultChange,
    handleSave,
    reset,
  };
};

export default useFilterNameForm;
